import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react'
import { useId } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FieldSkeleton } from '@/components/layout/field-row'
import { Collapse } from '@/components/ui/collapse'
import { Icon } from '@/components/ui/icon'
import { ProgressBar } from '@/components/ui/progress-bar'
import { Typography } from '@/components/ui/typography'
import { Building } from '@/reducers/types'

import {
  PlaceFeatureGroup,
  PlaceFeatureKey,
  PlaceFlags,
} from '../../features/places-features.constants'
import { getGroupSummary } from '../../features/places-features.summary'
import { PlaceFeatureCard } from './place-feature-card'
import * as S from './place-feature-collapsible-group.styled'
import {
  collectPlaceFeatureKeys,
  getPlaceFeatureTree,
  splitPlaceFeatureTreeByBranch,
} from './place-feature-tree'

interface PlaceFeatureGroupHeaderProps {
  bodyId?: string
  disabled?: boolean
  enabled?: number
  isOpen: boolean
  onToggle?: VoidFunction
  summary?: string
  title: string
  total?: number
}

// Note: without `enabled` / `total` (building still loading) the progress is a skeleton
const PlaceFeatureGroupHeader = ({
  bodyId,
  disabled,
  enabled,
  isOpen,
  onToggle,
  summary,
  title,
  total,
}: PlaceFeatureGroupHeaderProps) => {
  const { t } = useTranslation(['organisation'])
  const isLoaded = enabled !== undefined && total !== undefined

  return (
    <S.Header
      $isOpen={isOpen}
      aria-controls={bodyId}
      aria-expanded={isOpen}
      disabled={disabled}
      type="button"
      onClick={onToggle}
    >
      <S.HeaderMain>
        <S.ChevronBox>
          <Icon icon={isOpen ? CaretDownIcon : CaretRightIcon} size="1.6rem" />
        </S.ChevronBox>
        <S.HeaderText>
          <Typography size="text-large" weight="semibold" color="black800" as="span">
            {title}
          </Typography>
          {summary && <S.Summary>{summary}</S.Summary>}
        </S.HeaderText>
      </S.HeaderMain>
      <S.Progress>
        {isLoaded ? (
          <>
            <ProgressBar label={title} max={total} value={enabled} width="12rem" />
            <S.ProgressCount>
              {t('organisation:places.features.tab.enabledCount', { enabled, total })}
            </S.ProgressCount>
          </>
        ) : (
          <FieldSkeleton height="2.4rem" width="20rem" />
        )}
      </S.Progress>
    </S.Header>
  )
}

interface PlaceFeatureCollapsibleGroupProps {
  building: Building
  customLabels?: Partial<Record<PlaceFeatureKey, string>>
  errorFeatures?: string[]
  errorMessage?: string
  group: PlaceFeatureGroup
  isAdmin: boolean
  isEditing: boolean
  isOpen: boolean
  onToggle: VoidFunction
  // Note: a single-root group is shown as one card for the root and one card per provider branch
  splitByBranch?: boolean
}

export const PlaceFeatureCollapsibleGroup = ({
  building,
  customLabels,
  errorFeatures = [],
  errorMessage,
  group,
  isAdmin,
  isEditing,
  isOpen,
  onToggle,
  splitByBranch,
}: PlaceFeatureCollapsibleGroupProps) => {
  const { t } = useTranslation(['organisation'])
  const values = useWatch<PlaceFlags>()
  const bodyId = useId()
  const tree = getPlaceFeatureTree(group)

  if (!tree.length) return null

  const keys = collectPlaceFeatureKeys(tree)
  const labels = Object.fromEntries(
    keys.map((key) => [
      key,
      customLabels?.[key] ?? t(`organisation:places.building.toggles.${key}`),
    ]),
  )
  const { enabled, more, names, total } = getGroupSummary(labels, values)

  // Note: a group that is being edited, or holds a failed switch, cannot be collapsed
  const isForcedOpen =
    isEditing || errorFeatures.some((key) => keys.includes(key as PlaceFeatureKey))
  const isExpanded = isOpen || isForcedOpen

  const summary = enabled
    ? t('organisation:places.features.summary.on', {
        names: [
          names.join(', '),
          more ? t('organisation:places.features.summary.more', { extra: more }) : '',
        ]
          .filter(Boolean)
          .join(' '),
      })
    : t('organisation:places.features.summary.none')

  return (
    <S.Group>
      <PlaceFeatureGroupHeader
        bodyId={bodyId}
        disabled={isForcedOpen}
        enabled={enabled}
        isOpen={isExpanded}
        summary={summary}
        title={t(`organisation:places.features.groups.${group}`)}
        total={total}
        onToggle={onToggle}
      />
      <Collapse in={isExpanded} inert={!isExpanded}>
        <S.Body id={bodyId}>
          {(splitByBranch ? splitPlaceFeatureTreeByBranch(tree) : tree).map((node) => (
            <PlaceFeatureCard
              key={node.definition.key}
              building={building}
              customLabels={customLabels}
              errorFeatures={errorFeatures}
              errorMessage={errorMessage}
              isAdmin={isAdmin}
              isEditing={isEditing}
              node={node}
            />
          ))}
        </S.Body>
      </Collapse>
    </S.Group>
  )
}

export const PlaceFeatureCollapsibleGroupSkeleton = ({ group }: { group: PlaceFeatureGroup }) => {
  const { t } = useTranslation(['organisation'])

  return (
    <S.Group>
      <PlaceFeatureGroupHeader
        isOpen={false}
        title={t(`organisation:places.features.groups.${group}`)}
      />
    </S.Group>
  )
}
